/**
 * 核心概览摘要 Hook
 * 用于获取预生成的 AI 摘要数据
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import {
  getCoreOverviewSummary,
  ICoreOverviewSummaryRequest,
  ICoreOverviewSummaryResponse,
} from '@op/services/weeklyReport';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';

interface IUseCoreOverviewSummaryOptions {
  autoFetch?: boolean;
  hideSummary: boolean;
}

interface IRichTextTab {
  value: string;
  summary: string;
  summaryCn: string;
  text: string;
  textCn: string;
}

interface IUseCoreOverviewSummaryResult {
  /** RichTextBlock 组件需要的格式 */
  richText: IRichTextTab[];
  loading: boolean;
  error: boolean;
  fetchData: (params: ICoreOverviewSummaryRequest) => Promise<any>;
}

/**
 * 核心概览摘要 Hook
 * @param filter - 筛选参数
 * @param options - 配置项
 * @returns RichTextBlock 格式数据、加载状态、错误状态和请求函数
 */
export const useCoreOverviewSummary = (
  filter?: ICoreOverviewSummaryRequest,
  options?: IUseCoreOverviewSummaryOptions,
): IUseCoreOverviewSummaryResult => {
  const { autoFetch = true, hideSummary = false } = options || {};
  const [richText, setRichText] = useState<IRichTextTab[]>([]);
  const [hideSummaryLoading, setHideSummaryLoading] = useState(false);
  const hideSummaryTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const {
    loading: requestLoading,
    error,
    wrapperCancelRequest,
  } = useCancelRequestHook({
    clear: () => setRichText([]),
  });
  const loading = requestLoading || hideSummaryLoading;

  const fetchData = useCallback((params: ICoreOverviewSummaryRequest) => {
    return wrapperCancelRequest(getCoreOverviewSummary, params)
      .then(res => {
        const summaryData = res as ICoreOverviewSummaryResponse;
        setRichText([
          {
            value: 'overview',
            text: 'AI Summary',
            textCn: '核心概览',
            summary: summaryData.report_en || summaryData.report_cn || '',
            summaryCn: summaryData.report_cn || '',
          },
        ]);
        return res;
      })
      .catch(err => {
        console.error('获取核心概览摘要失败:', err);
      });
  }, []);

  // 自动请求：filter 存在且 autoFetch 为 true 时才请求
  useEffect(() => {
    if (!filter || !autoFetch) return;
    if (hideSummary) {
      setHideSummaryLoading(true);
      hideSummaryTimerRef.current = setTimeout(() => {
        setRichText([
          {
            value: 'overview',
            text: 'AI Summary',
            textCn: '核心概览',
            summary: '',
            summaryCn: '',
          },
        ]);
        setHideSummaryLoading(false);
      }, 300);
      return () => clearTimeout(hideSummaryTimerRef.current);
    }
    fetchData(filter);
  }, [filter, autoFetch, hideSummary, fetchData]);

  return { richText, loading, error, fetchData };
};

export default useCoreOverviewSummary;
